/**
 * Edit a point (owner or staff).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import PointForm from '../../components/points/PointForm';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import pointService from '../../services/pointService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { canManageOwnedResource } from '../../utils/resourcePermissions';
import { getSlugFromParams } from '../../utils/urlHelpers';

const PointEditPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const pointSlug = getSlugFromParams(params, 'point');
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { showError } = useToast();
  const [point, setPoint] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pointSlug) return;
    let cancelled = false;
    pointService
      .getPoint(pointSlug)
      .then((data) => {
        if (!cancelled) setPoint(data);
      })
      .catch(() => {
        if (!cancelled) setPoint(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pointSlug]);

  useEffect(() => {
    if (authLoading || loading || !point || !user || !isAuthenticated) return;
    if (!canManageOwnedResource(user, point)) {
      showError('You do not have permission to edit this point.');
      navigate(`/points/${point.slug}`, { replace: true });
    }
  }, [authLoading, loading, point, user, isAuthenticated, navigate, showError]);

  if (authLoading || loading) {
    return (
      <PageLayout title="Edit Point">
        <div className="min-h-[40vh] flex items-center justify-center">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    navigate('/login', { state: { from: `/points/${pointSlug}/edit` } });
    return null;
  }

  if (!point) {
    return (
      <PageLayout title="Point not found">
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-gray-600 mb-4">This point could not be found.</p>
          <button type="button" onClick={() => navigate('/points')} className="text-emerald-700 font-medium">
            Back to Points
          </button>
        </div>
      </PageLayout>
    );
  }

  if (!canManageOwnedResource(user, point)) {
    return (
      <PageLayout title="Edit Point">
        <div className="min-h-[40vh] flex items-center justify-center">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={`Edit ${point.name}`}>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(`/points/${point.slug}`)}
          className="text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          ← Back to point
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Edit point</h1>
        <PointForm mode="edit" initialPoint={point} />
      </div>
    </PageLayout>
  );
};

export default PointEditPage;
